import {
  DASHBOARD_TILE_IDS,
  type DashboardPreferences,
  type DashboardSelectedDevice,
  type DashboardTileId,
} from './dashboard.types';

const STORAGE_KEY_PREFIX = 'vnls.dashboard.preferences.v1';
const MAX_SELECTED_DEVICES = 3;
const MAX_TELEMETRY_IDS = 2;
const dashboardTileIds = new Set<string>(DASHBOARD_TILE_IDS);

type DashboardPreferencesStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const DEFAULT_DASHBOARD_PREFERENCES: DashboardPreferences = {
  version: 1,
  hiddenTileIds: [],
  selectedDevices: [],
};

export function createDashboardPreferencesStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${userId}`;
}

function uniqueStrings(value: unknown, limit?: number): string[] {
  if (!Array.isArray(value)) return [];

  const strings = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);

  return [...new Set(strings)].slice(0, limit);
}

function normalizeSelectedDevices(value: unknown): DashboardSelectedDevice[] {
  if (!Array.isArray(value)) return [];

  return value.reduce<DashboardSelectedDevice[]>((devices, item) => {
    if (devices.length >= MAX_SELECTED_DEVICES || !item || typeof item !== 'object') {
      return devices;
    }

    const candidate = item as Record<string, unknown>;
    const manufacturer =
      typeof candidate.manufacturer === 'string' ? candidate.manufacturer.trim() : '';
    const serialNumber =
      typeof candidate.serialNumber === 'string' ? candidate.serialNumber.trim() : '';

    const isDuplicate = devices.some(
      (device) =>
        device.manufacturer === manufacturer && device.serialNumber === serialNumber,
    );

    if (!manufacturer || !serialNumber || isDuplicate) {
      return devices;
    }

    return [
      ...devices,
      {
        manufacturer,
        serialNumber,
        telemetryIds: uniqueStrings(candidate.telemetryIds, MAX_TELEMETRY_IDS),
      },
    ];
  }, []);
}

export function normalizeDashboardPreferences(value: unknown): DashboardPreferences {
  if (!value || typeof value !== 'object') return DEFAULT_DASHBOARD_PREFERENCES;

  const candidate = value as Record<string, unknown>;
  if (candidate.version !== 1) return DEFAULT_DASHBOARD_PREFERENCES;

  const hiddenTileIds = uniqueStrings(candidate.hiddenTileIds).filter(
    (tileId): tileId is DashboardTileId => dashboardTileIds.has(tileId),
  );

  return {
    version: 1,
    hiddenTileIds,
    selectedDevices: normalizeSelectedDevices(candidate.selectedDevices),
  };
}

function browserStorage(): DashboardPreferencesStorage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function readDashboardPreferences(
  userId?: string,
  storage = browserStorage(),
): DashboardPreferences {
  if (!userId || !storage) return DEFAULT_DASHBOARD_PREFERENCES;

  try {
    const storedPreferences = storage.getItem(
      createDashboardPreferencesStorageKey(userId),
    );
    return storedPreferences
      ? normalizeDashboardPreferences(JSON.parse(storedPreferences))
      : DEFAULT_DASHBOARD_PREFERENCES;
  } catch {
    return DEFAULT_DASHBOARD_PREFERENCES;
  }
}

export function writeDashboardPreferences(
  userId: string | undefined,
  preferences: DashboardPreferences,
  storage = browserStorage(),
): boolean {
  if (!userId || !storage) return false;

  try {
    // TODO: Migrate dashboard preferences to the user-preferences backend so they sync
    // across browsers and devices.
    storage.setItem(
      createDashboardPreferencesStorageKey(userId),
      JSON.stringify(normalizeDashboardPreferences(preferences)),
    );
    return true;
  } catch {
    return false;
  }
}
